import useSWRMutation from "swr/mutation";
import { mutate as globalMutate } from "swr";
import { useCosmosClient } from "@/services/cosmos";
import { type CheckoutSession } from "@/types";
import { CksCheckoutStateSchema } from "@/services/cosmos/schema/checkout-service";
import { logError, logInfo } from "@/services/logging/logger";
import {
  handleHttpError,
  type ApiError,
} from "@/lib/http-error-recovery";

/**
 * Mutation hook to navigate back from payment method selection.
 *
 * Behavioral contract (must not change):
 * - POSTs via cosmosClient.backFromPaymentMethodSelection
 * - On error: attempts smart recovery via handleHttpError (identical pattern)
 * - If recovery succeeds: returns fresh session
 * - If recovery fails: updates SWR cache with error state, throws enhanced error
 */
export function useBackFromPaymentMethodSelection() {
  const cosmosClient = useCosmosClient();

  return useSWRMutation(
    "session",
    async (): Promise<CheckoutSession> => {
      const currentSession = await globalMutate(
        "session",
        (current: CheckoutSession | undefined) => current,
        { revalidate: false },
      );

      try {
        return await cosmosClient.backFromPaymentMethodSelection();
      } catch (err: unknown) {
        logError(
          "checkout-session",
          "Error going back from payment method selection",
          err instanceof Error ? err : new Error(String(err)),
          { error: err },
        );

        const recoveryResult = await handleHttpError(err as ApiError, {
          refetchSession: async () => {
            const freshData = await cosmosClient.getCheckoutSessionV3();
            return CksCheckoutStateSchema.parse(freshData);
          },
          currentSession: currentSession ?? undefined,
          endpoint: "back-from-payment-method-selection",
          httpMethod: "POST",
          requestContext: {},
        });

        if (recoveryResult.recovered) {
          logInfo(
            "checkout-session",
            "Recovered from back-from-payment-method error",
            {
              originalError: recoveryResult.originalError.response?.status,
              recoveredSessionStatus: recoveryResult.session.status,
            },
          );
          return recoveryResult.session;
        }

        await globalMutate("session", recoveryResult.session, {
          revalidate: false,
        });

        const enhancedError = Object.assign(err as Error, {
          recoveryAttempted: true,
          recoveryResult,
        });
        throw enhancedError;
      }
    },
    {
      populateCache: (newVal: CheckoutSession) => newVal,
      onError: (error: unknown) => {
        const enhanced = error as { recoveryAttempted?: boolean };
        if (!enhanced.recoveryAttempted) {
          logError(
            "checkout-session",
            "Error going back from payment method selection (onError)",
            error instanceof Error ? error : new Error(String(error)),
            { error },
          );
        }
      },
      revalidate: false,
    },
  );
}
